import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Bonus, Slider } from '@atlas/components';
import { AccountManager, SiteConfigManager } from '@atlas/core';
import { Icon, Label, Timer, ToggleCheckbox } from '@atlas/ui';

import { CDN_TYPE_CRM, imageUrlCDN } from 'common/utils/utils';
import { BASE_PATH, bonusStatuses, DAY } from 'src/common/constants';
import { useDepositBonusContext } from 'src/components/ClientProfile/Payment/Deposit/DepositBonusContext';
import useBonusSliderAtEnd from 'src/hooks/useBonusSliderAtEnd';
import useLanguage from 'src/hooks/useLanguage';

const timerLabels = {
  days: 'timer_d',
  hours: 'timer_h',
  minutes: 'timer_m',
};

const DepositBonuses = () => {
  const { alpha3Code } = useLanguage();
  const hideSportBonuses = SiteConfigManager.getConfig('config.config.general.hideSportBonuses');
  const { activatedBonus, bonusesList, isBonusesDisabled, selectedBonus, setSelectedBonus } =
    useDepositBonusContext();

  const bonuses = useMemo(() => activatedBonus ?? bonusesList, [activatedBonus, bonusesList]);
  const bonusList = useMemo(() => {
    const list = bonuses ? [bonuses].flat() : [];

    return list.map(b => {
      const nested = b.bonus || {};

      return {
        ...nested,
        ...b,
        currency: b.currency ?? nested.currency,
        financeDataList: b.financeDataList ?? nested.financeDataList ?? [],
        name: b.name ?? nested.name,
      };
    });
  }, [bonuses]);

  const currency = AccountManager.getAccountCurrency();
  const { atEnd: bonusSliderAtEnd, onSwiper: onBonusSwiper } = useBonusSliderAtEnd();

  const handleSelectBonus = useCallback(
    bonus => {
      const isActive = selectedBonus?.id === bonus.id;
      const newActiveBonus = isActive ? null : bonus;

      setSelectedBonus(newActiveBonus);
    },
    [selectedBonus, setSelectedBonus],
  );

  // for re-render slider
  const sliderKey = useMemo(() => bonusList.map(({ id }) => id).join('-'), [bonusList]);

  const hasSingleBonus = bonusList.length === 1;

  const sliderConfig = useMemo(
    () => ({
      autoHeight: false,
      autoplay: false,
      loop: false,
      nextBtn: <Slider.NextBtn>next</Slider.NextBtn>,
      prevBtn: <Slider.PrevBtn>prev</Slider.PrevBtn>,
      slidesPerView: 'auto',
      spaceBetween: '8px',
    }),
    [],
  );

  if (!bonusList.length) {
    return null;
  }

  const renderBonus = bonus => {
    const finance = bonus.financeDataList.find(item => item.currency === currency);
    const bonusImage = bonus?.localizedImageInfoList?.find(
      item => item.language.toLowerCase() === alpha3Code.toLowerCase(),
    );

    const imageLink = bonusImage?.imageLink
      ? `${imageUrlCDN(bonusImage.imageLink, CDN_TYPE_CRM)}`
      : `${BASE_PATH}/static/assets/images/gift.svg`;

    const bonusTitle = <h4 className="deposit-bonus-title">{bonus.name}</h4>;
    const isActive = selectedBonus?.id === bonus.id;

    const bonusDescription = (
      <div className="deposit-bonus-description">
        <Label
          message="deposit_min_amount"
          params={{
            currency: bonus.currency,
            minBonusAmount: finance?.minDeposit,
          }}
        />
      </div>
    );

    const getTimer = () => {
      if (bonus.expirationTime) return bonus.expirationTime;

      if (
        bonus?.status === bonusStatuses.ACTIVATED ||
        bonus?.bonusStatus === bonusStatuses.ACTIVE
      ) {
        return bonus.createTime != null ? bonus.createTime + 3 * DAY : (bonus.endTime ?? null);
      }

      return null;
    };

    const disableBlock = (
      <div className="bonus-disable">
        <Icon className="disabled-icon" name="lock" />
        <Label className="bonus-disable-title" message="deposit_bonus_unavailable_title" />
        <Label className="bonus-disable-description" message="deposit_bonus_unavaliable_message" />
      </div>
    );

    const timer = getTimer() && (
      <div className="timer">
        <Icon className="deposit-timer-icon" height={16} name="time" width={16} />
        <Timer labels={timerLabels} separator=":" showSeconds={false} time={getTimer()} />
      </div>
    );

    const bonusLabel = !hideSportBonuses && (
      <Label className="deposit-bonus-label" message={`bonus_product_${bonus.productType}`} />
    );

    return (
      <div
        className={classNames('deposit-bonus', {
          ['deposit-bonus--single']: hasSingleBonus && isBonusesDisabled,
        })}
        key={bonus.id}
      >
        <Bonus
          active={isActive}
          description={bonusDescription}
          disabled={bonus.disabled || false}
          disabledBlock={disableBlock}
          image={imageLink}
          label={bonusLabel}
          timer={timer}
          title={bonusTitle}
          toggleCheckbox={
            <ToggleCheckbox
              checked={isActive}
              disabled={isBonusesDisabled}
              onChange={() => handleSelectBonus(bonus)}
            />
          }
        />
      </div>
    );
  };

  return (
    <div
      className={classNames('deposit-bonus-container', {
        single: hasSingleBonus && isBonusesDisabled,
      })}
    >
      {!hasSingleBonus && (
        <div className="slider-head">
          <Label className="slider-title" message="toggle_to_select" />
          <Label
            className="slider-description"
            message="deposit_to_activate"
            params={{
              currency,
              minBonusAmount: 3000,
            }}
          />
        </div>
      )}
      {hasSingleBonus && renderBonus(bonusList[0])}
      {!hasSingleBonus && (
        <div
          className={classNames('bonus-slider-outer', {
            'bonus-slider-outer--at-end': bonusSliderAtEnd,
          })}
        >
          <Slider key={sliderKey} {...sliderConfig} onSwiper={onBonusSwiper}>
            {bonusList.map(bonus => (
              <Slider.Slide className="deposit-bonus-slide" key={bonus.id}>
                {renderBonus(bonus)}
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );
};

const BonusScheme = PropTypes.shape({
  currency: PropTypes.string,
  disabled: PropTypes.bool,
  financeDataList: PropTypes.arrayOf(
    PropTypes.shape({
      currency: PropTypes.string.isRequired,
      minDeposit: PropTypes.number.isRequired,
    }),
  ),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  image: PropTypes.string,
  name: PropTypes.string.isRequired,
});

DepositBonuses.propTypes = {
  bonuses: PropTypes.oneOfType([BonusScheme, PropTypes.arrayOf(BonusScheme)]).isRequired,
  isBonusesDisabled: PropTypes.bool,
  onSelectBonus: PropTypes.func.isRequired,
  selectedBonus: BonusScheme,
};

export default DepositBonuses;
